import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { ChevronDown, ChevronRight, ArrowRight, Star, Pencil } from 'lucide-react';
import { MarkdownWithCitations } from './citations/MarkdownWithCitations';
import { RedactionText } from './redaction/RedactionText';
import { fromRAGCitation } from '../types/citation';
import type { RAGCitation } from '../types';
import { formatClinicalDescription } from '../utils/formatDescription';
import type { ChronologyNarrative, NarrativeSection, EventAnnotation } from '../services/chronologyApi';
import type { SortMode } from './tabs/ChronologyTab';
import { EntryActions } from './ask/EntryActions';
import type { ContextItem } from '../contexts/AskSidebarContext';
import { formatChronologyEvent, formatChronologyEventHtml } from '../utils/copyFormatters';
import { CorrectionReasonModal, EVENT_TYPE_OPTIONS, EditedFields } from './review/InlineEventEditForm';
import { OriginalContentPopover } from './review/OriginalContentPopover';
import { parsePiiMarkers } from '../utils/parsePiiMarkers';
import { useKeyFindings } from '../contexts/KeyFindingsContext';
import { useHighlightParam } from '../hooks/useHighlightParam';
import { AnnotatedEventText } from './review/AnnotatedEventText';

function getEventDateStr(timestamp: any): string | null {
  if (!timestamp) return null;
  if (typeof timestamp === 'string') return timestamp;
  return timestamp.start ?? null;
}

interface ChronologyNarrativeViewProps {
  caseId: string;
  narrative: ChronologyNarrative;
  events: any[];
  totalEventCount: number;
  formatEventDate: (timestamp: string | { start: string; end: string }) => string;
  getEventTypeBadge: (type: string) => string;
  getEventTypeBadgeColor: (type: string) => string;
  stripTypePrefix: (description: string, type: string) => string;
  onViewSource?: (documentId: string, pageNumber?: number) => void;
  onSwitchToTimeline?: () => void;
  monthRefs?: Map<string, HTMLDivElement>;
  sortMode?: SortMode;
  /** Callback to toggle explain for an event */
  onExplain?: (eventId: string) => void;
  /** Currently-explained event ID (or null) */
  explainEventId?: string | null;
  /** Event ID currently loading explanation (or null) */
  explainLoading?: string | null;
  /** Cached annotations keyed by event ID */
  eventAnnotations?: Record<string, EventAnnotation[]>;
  /** Cached source summaries keyed by event ID */
  eventSourceSummaries?: Record<string, string>;
  /** Submit a question to the AI assistant */
  submitAskMessage?: (message: string) => void;
}

export const ChronologyNarrativeView: React.FC<ChronologyNarrativeViewProps> = ({
  caseId,
  narrative,
  events,
  totalEventCount,
  formatEventDate,
  getEventTypeBadge,
  getEventTypeBadgeColor,
  stripTypePrefix,
  onViewSource,
  onSwitchToTimeline,
  monthRefs,
  sortMode = 'date-desc',
  onExplain,
  explainEventId = null,
  explainLoading = null,
  eventAnnotations = {},
  eventSourceSummaries = {},
  submitAskMessage,
}) => {
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  // All sections start collapsed — user expands to drill into events
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());

  // Editing state
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<EditedFields>({});
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [showOriginalForEventId, setShowOriginalForEventId] = useState<string | null>(null);

  // Build a lookup map from eventId to event object
  const eventMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const event of events) {
      map.set(event.eventId, event);
    }
    return map;
  }, [events]);

  // Sections filtered and sorted by current sort mode
  const sortedSections = useMemo(() => {
    const sigOrder: Record<string, number> = { MAJOR: 0, SUPPORTING: 1, ROUTINE: 2 };

    const withMeta = narrative.sections.map((section, index) => {
      const sectionEvents = section.eventIds
        .map(id => eventMap.get(id))
        .filter(Boolean);
      // Representative values for sorting the section
      let representativeTime = 0;
      let representativeSig = 2;
      for (const ev of sectionEvents) {
        const t = new Date(getEventDateStr(ev.timestamp) || 0).getTime();
        if (sortMode === 'date-asc') {
          representativeTime = representativeTime === 0 ? t : Math.min(representativeTime, t);
        } else {
          representativeTime = Math.max(representativeTime, t);
        }
        const s = sigOrder[ev.significance || 'MAJOR'] ?? 2;
        representativeSig = Math.min(representativeSig, s);
      }
      return { section, originalIndex: index, eventCount: sectionEvents.length, representativeTime, representativeSig };
    }).filter(s => s.eventCount > 0);

    const sorted = [...withMeta];
    switch (sortMode) {
      case 'date-asc':
        sorted.sort((a, b) => a.representativeTime - b.representativeTime);
        break;
      case 'date-desc':
        sorted.sort((a, b) => b.representativeTime - a.representativeTime);
        break;
    }
    return sorted;
  }, [narrative.sections, eventMap, sortMode]);

  // Count how many narrative events actually exist in the event data
  const narrativeEventCount = useMemo(() => {
    let count = 0;
    for (const section of narrative.sections) {
      for (const id of section.eventIds) {
        if (eventMap.has(id)) count++;
      }
    }
    return count;
  }, [narrative.sections, eventMap]);

  // For Quick Jump: determine which eventId is the first occurrence of each month
  // across the narrative section order, so we can register a ref on it.
  const firstEventPerMonth = useMemo(() => {
    const seen = new Set<string>();
    const result = new Map<string, string>(); // eventId → monthKey
    for (const section of narrative.sections) {
      for (const id of section.eventIds) {
        const event = eventMap.get(id);
        if (!event) continue;
        const raw = getEventDateStr(event.timestamp);
        if (!raw) continue;
        const d = new Date(raw);
        if (isNaN(d.getTime())) continue;
        const key = `${d.getFullYear()}-${d.getMonth()}`;
        if (!seen.has(key)) {
          seen.add(key);
          result.set(id, key);
        }
      }
    }
    return result;
  }, [narrative.sections, eventMap]);

  const toggleEvent = (eventId: string) => {
    setExpandedEvents(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  // Auto-expand section containing a highlighted event and scroll to it
  const { highlightId, scrollToHighlighted } = useHighlightParam();
  useEffect(() => {
    if (!highlightId) return;
    // Find which section (by originalIndex) contains this eventId
    for (const { section, originalIndex } of sortedSections) {
      if (section.eventIds.includes(highlightId)) {
        setExpandedSections(prev => new Set(prev).add(originalIndex));
        requestAnimationFrame(() => scrollToHighlighted());
        return;
      }
    }
  }, [highlightId, sortedSections, scrollToHighlighted]);

  const toggleSection = (index: number) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const { isEventMarked } = useKeyFindings();

  const renderEventCard = (event: any, isIndex: boolean) => {
    const eventType = event.type || 'unknown';
    const isExpanded = expandedEvents.has(event.eventId);
    const hasSummary = event.summary && event.summary !== event.description;
    const displayText = !isExpanded || !hasSummary
      ? formatClinicalDescription(stripTypePrefix(event.description || '', eventType))
      : formatClinicalDescription(stripTypePrefix(event.summary || event.description || '', eventType));
    const isEditing = editingEventId === event.eventId;
    const eventIsMarked = isEventMarked(event.eventId);
    const isExplaining = explainEventId === event.eventId;
    const isExplainLoading = explainLoading === event.eventId;

    // Register a Quick Jump ref on the first event for each month
    const monthKey = firstEventPerMonth.get(event.eventId);
    const refCallback = monthKey && monthRefs
      ? (el: HTMLDivElement | null) => { if (el) monthRefs.set(monthKey, el); }
      : undefined;

    return (
      <div
        key={event.eventId}
        data-event-id={event.eventId}
        ref={refCallback}
        style={isExplainLoading ? { animation: 'border-pulse 1.5s ease-in-out infinite' } : undefined}
        className={`rounded-lg border transition-all duration-150 scroll-mt-20 ${
          isExplainLoading
            ? 'bg-white dark:bg-medical-800'
            : isExplaining
              ? 'border-emerald-300 dark:border-emerald-700 shadow-sm bg-white dark:bg-medical-800'
              : eventIsMarked
                ? 'border-amber-300 dark:border-amber-600 shadow-sm bg-white dark:bg-medical-800'
                : isIndex
                  ? 'border-primary-300 dark:border-primary-600 bg-primary-50/50 dark:bg-primary-900/20'
                  : 'border-medical-200 dark:border-medical-700 bg-white dark:bg-medical-800'
        }`}
      >
        <div className="px-4 py-3">
          {/* Header row */}
          <div className="flex items-center gap-2 mb-1.5">
            <EntryActions
              compact
              item={{
                id: event.eventId,
                type: 'event' as ContextItem['type'],
                summary: event.title || stripTypePrefix(event.description || '', eventType).replace(/\[\d+\]/g, '').slice(0, 80),
                source: `Chronology — ${getEventTypeBadge(eventType)}`,
                data: { eventId: event.eventId, type: eventType, timestamp: event.timestamp, description: event.description },
              }}
              className="mr-1"
              onCopy={() => ({ text: formatChronologyEvent(event), html: formatChronologyEventHtml(event) })}
              onExplain={onExplain ? () => onExplain(event.eventId) : undefined}
              isExplaining={isExplaining}
              isExplainLoading={isExplainLoading}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isEditing) {
                  setEditingEventId(null);
                  setEditValues({});
                } else {
                  setEditingEventId(event.eventId);
                  setEditValues({
                    headline: parsePiiMarkers(event.title || '').cleanText.replace(/\s*\[\d+\]/g, ''),
                    summary: parsePiiMarkers(event.summary || '').cleanText.replace(/\s*\[\d+\]/g, ''),
                    description: parsePiiMarkers(event.description || '').cleanText.replace(/\s*\[\d+\]/g, ''),
                    eventDate: getEventDateStr(event.timestamp)?.slice(0, 16) || '',
                    eventType: eventType,
                  });
                }
              }}
              className="inline-flex items-center justify-center w-6 h-6 rounded-md text-medical-400 dark:text-medical-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors"
              title="Edit event"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            {isEditing ? (
              <input
                type="datetime-local"
                value={editValues.eventDate || ''}
                onChange={(e) => { e.stopPropagation(); setEditValues(v => ({ ...v, eventDate: e.target.value })); }}
                onClick={(e) => e.stopPropagation()}
                className="text-xs tabular-nums bg-white dark:bg-medical-700 border border-primary-300 dark:border-primary-600 text-medical-900 dark:text-medical-100 rounded px-1.5 py-0.5 outline-none"
              />
            ) : (
              <span className="text-xs font-medium text-medical-500 dark:text-medical-400">
                {formatEventDate(event.timestamp)}
              </span>
            )}
            {isEditing ? (
              <select
                value={editValues.eventType || eventType}
                onChange={(e) => { e.stopPropagation(); setEditValues(v => ({ ...v, eventType: e.target.value })); }}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white dark:bg-medical-700 border border-primary-300 dark:border-primary-600 text-medical-900 dark:text-medical-100 outline-none cursor-pointer"
              >
                {EVENT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            ) : (
              <span
                className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${getEventTypeBadgeColor(eventType)}`}
              >
                {getEventTypeBadge(eventType)}
              </span>
            )}
            {(event.validationStatus === 'VALIDATED' || event.isEdited) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowOriginalForEventId(
                    showOriginalForEventId === event.eventId ? null : event.eventId
                  );
                }}
                className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-800/60 cursor-pointer transition-colors"
                title="Click to view original content"
              >
                Edited
              </button>
            )}
            {isIndex && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-400">
                <Star className="h-3 w-3" />
                Index
              </span>
            )}
            {event.significance && (
              <span className={`text-[10px] font-medium uppercase ${
                event.significance === 'MAJOR'
                  ? 'text-red-600 dark:text-red-400'
                  : event.significance === 'SUPPORTING'
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-medical-400 dark:text-medical-500'
              }`}>
                {event.significance}
              </span>
            )}
          </div>

          {/* Headline */}
          {isEditing ? (
            <input
              type="text"
              value={editValues.headline || ''}
              onChange={(e) => setEditValues(v => ({ ...v, headline: e.target.value }))}
              onClick={(e) => e.stopPropagation()}
              placeholder="Headline"
              maxLength={200}
              className="w-full text-sm font-semibold text-medical-900 dark:text-medical-100 mb-1 bg-white dark:bg-medical-700 border border-primary-300 dark:border-primary-600 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-primary-400"
            />
          ) : event.title ? (
            <h4 className="text-sm font-semibold text-medical-900 dark:text-medical-100 mb-1">
              {isExplaining && eventAnnotations[event.eventId] ? (
                <AnnotatedEventText text={event.title} annotations={eventAnnotations[event.eventId]} />
              ) : (
                <RedactionText text={event.title} />
              )}
            </h4>
          ) : null}

          {/* Clinician / Location */}
          {(event.clinician || event.location) && (
            <p className="text-xs text-medical-500 dark:text-medical-400 mb-1.5">
              {event.clinician && <RedactionText text={event.clinician} />}
              {event.clinician && event.location && ' · '}
              {event.location && <RedactionText text={event.location} />}
            </p>
          )}

          {/* Description */}
          {isEditing ? (
            <div onClick={(e) => e.stopPropagation()}>
              <textarea
                value={editValues.description || ''}
                onChange={(e) => setEditValues(v => ({ ...v, description: e.target.value }))}
                maxLength={2000}
                rows={4}
                className="w-full text-sm text-medical-900 dark:text-medical-100 leading-relaxed bg-white dark:bg-medical-700 border border-primary-300 dark:border-primary-600 rounded px-2.5 py-1.5 outline-none focus:ring-1 focus:ring-primary-400 resize-y"
              />
            </div>
          ) : (
            <div className="text-sm text-medical-700 dark:text-medical-300 prose prose-sm dark:prose-invert max-w-none">
              {isExplaining && eventAnnotations[event.eventId] ? (
                <AnnotatedEventText
                  text={displayText}
                  annotations={eventAnnotations[event.eventId]}
                />
              ) : (
                <MarkdownWithCitations
                  content={displayText}
                  citations={event.citations?.map((c: any) => fromRAGCitation(c)) || []}
                />
              )}
            </div>
          )}

          {/* Annotation legend + provenance summary */}
          {isExplaining && eventAnnotations[event.eventId]?.length > 0 && (
            <div className="mt-3 pt-2.5 border-t border-medical-100 dark:border-medical-700/50 space-y-2">
              {/* Legend */}
              <div className="flex items-center gap-3 text-[11px] text-medical-500 dark:text-medical-400">
                <span className="flex items-center gap-1.5">
                  <span className="w-5 border-b-2 border-emerald-400 dark:border-emerald-500 border-solid" />
                  <span className="font-medium">Verbatim</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-5 border-b-2 border-amber-400 dark:border-amber-500 border-dashed" />
                  <span className="font-medium">Summarised</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-5 border-b-2 border-indigo-400 dark:border-indigo-500 border-dotted" />
                  <span className="font-medium">Synthesised</span>
                </span>
                <span className="ml-auto text-[10px] italic text-medical-400 dark:text-medical-500">Hover for details · Click to view source</span>
              </div>

              {/* Provenance prose summary */}
              {eventSourceSummaries[event.eventId] && (
                <div className="rounded-lg bg-medical-50 dark:bg-medical-700/30 border border-medical-100 dark:border-medical-700/50 px-3.5 py-2.5">
                  <p className="text-xs leading-relaxed text-medical-600 dark:text-medical-400">
                    {eventSourceSummaries[event.eventId]}
                  </p>
                  {submitAskMessage && (
                    <button
                      className="mt-2 text-[11px] font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        submitAskMessage(`Tell me more about the event "${event.title || event.description?.slice(0, 60)}" on ${formatEventDate(event.timestamp || '')}. What do the source records say and what is clinically significant?`);
                      }}
                    >
                      Dive deeper with ClinLexis →
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Save / Cancel bar when editing */}
          {isEditing && (
            <div className="mt-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => {
                  const changes: EditedFields = {};
                  if (editValues.headline !== (event.title || '')) changes.headline = editValues.headline;
                  if (editValues.description !== (event.description || '')) changes.description = editValues.description;
                  if (editValues.eventDate !== (getEventDateStr(event.timestamp)?.slice(0, 16) || '')) changes.eventDate = editValues.eventDate;
                  if (editValues.eventType !== eventType) changes.eventType = editValues.eventType;
                  if (Object.keys(changes).length === 0) {
                    setEditingEventId(null);
                    setEditValues({});
                    return;
                  }
                  setEditValues(changes);
                  setShowCorrectionModal(true);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors"
              >
                Save
              </button>
              <button
                onClick={() => { setEditingEventId(null); setEditValues({}); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-medical-600 dark:text-medical-300 hover:bg-medical-100 dark:hover:bg-medical-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Correction reason modal */}
          {isEditing && showCorrectionModal && (
            <CorrectionReasonModal
              eventId={event.eventId}
              caseId={caseId}
              changes={editValues}
              onClose={() => setShowCorrectionModal(false)}
              onSaved={() => {
                setShowCorrectionModal(false);
                setEditingEventId(null);
                setEditValues({});
              }}
            />
          )}

          {/* Original content popover */}
          {showOriginalForEventId === event.eventId && (
            <OriginalContentPopover
              event={event}
              caseId={caseId}
              onClose={() => setShowOriginalForEventId(null)}
              onReverted={() => setShowOriginalForEventId(null)}
            />
          )}
        </div>
      </div>
    );
  };

  const sortEvents = useCallback((evts: any[]) => {
    if (evts.length <= 1) return evts;
    const sorted = [...evts];
    switch (sortMode) {
      case 'date-asc':
        sorted.sort((a, b) => {
          const ta = new Date(getEventDateStr(a.timestamp) || 0).getTime();
          const tb = new Date(getEventDateStr(b.timestamp) || 0).getTime();
          return ta - tb;
        });
        break;
      case 'date-desc':
        sorted.sort((a, b) => {
          const ta = new Date(getEventDateStr(a.timestamp) || 0).getTime();
          const tb = new Date(getEventDateStr(b.timestamp) || 0).getTime();
          return tb - ta;
        });
        break;
    }
    return sorted;
  }, [sortMode]);

  const renderSection = (section: NarrativeSection, index: number) => {
    const isExpanded = expandedSections.has(index);
    const sectionEvents = sortEvents(
      section.eventIds
        .map(id => eventMap.get(id))
        .filter(Boolean)
    );

    // Check if this section contains the index event
    const hasIndexEvent = section.eventIds.includes(narrative.indexEventId);
    const isExpert = section.isExpertSection === true;

    return (
      <div key={index} className={`mb-8 ${isExpert ? 'border-l-4 border-purple-300 dark:border-purple-700 pl-4' : ''}`}>
        {/* Expert section label */}
        {isExpert && (
          <div className="mb-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
              Medico-Legal Opinion
            </span>
            <span className="ml-2 text-xs text-medical-400 dark:text-medical-500 italic">
              Not part of the clinical record
            </span>
          </div>
        )}
        {/* Section header */}
        <button
          onClick={() => toggleSection(index)}
          className="flex items-center gap-2 mb-3 group w-full text-left"
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-medical-400 dark:text-medical-500 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-medical-400 dark:text-medical-500 flex-shrink-0" />
          )}
          <h3 className={`text-base font-semibold ${
            isExpert
              ? 'text-purple-700 dark:text-purple-400'
              : hasIndexEvent
                ? 'text-primary-700 dark:text-primary-400'
                : 'text-medical-800 dark:text-medical-200'
          }`}>
            {section.title}
          </h3>
          <div className="flex-1 border-t border-medical-200 dark:border-medical-700 ml-2" />
          <span className="text-xs text-medical-400 dark:text-medical-500 flex-shrink-0">
            {sectionEvents.length} event{sectionEvents.length !== 1 ? 's' : ''}
          </span>
        </button>

        {/* Narrative summary — click to expand/collapse */}
        <div
          onClick={() => toggleSection(index)}
          className="text-sm text-medical-600 dark:text-medical-400 leading-relaxed mb-4 ml-6 cursor-pointer hover:text-medical-800 dark:hover:text-medical-200 transition-colors"
        >
          <RedactionText text={section.summary} />
        </div>

        {/* Event cards — shown when expanded */}
        {isExpanded && (
          <div className="space-y-2 ml-6">
            {sectionEvents.map(event =>
              renderEventCard(event, event.eventId === narrative.indexEventId)
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Overview summary */}
      <div className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 rounded-xl border border-amber-200 dark:border-amber-800 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-3">
          At a Glance
        </h2>
        <p className="text-sm text-medical-700 dark:text-medical-300 leading-relaxed">
          <RedactionText text={narrative.overviewSummary} />
        </p>
      </div>

      {/* Narrative sections (hide sections with no events after filtering, sorted by sort mode) */}
      {sortedSections.map(({ section, originalIndex }) =>
        renderSection(section, originalIndex)
      )}

      {/* Footer with event count and link to full chronology */}
      <div className="flex items-center justify-between pt-4 border-t border-medical-200 dark:border-medical-700">
        <p className="text-xs text-medical-400 dark:text-medical-500">
          Showing {narrativeEventCount} of {totalEventCount} events
        </p>
        {onSwitchToTimeline && (
          <button
            onClick={onSwitchToTimeline}
            className="flex items-center gap-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
          >
            View full chronology
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};
